<template>
  <div class="tp-page bg-light-page">

    <!-- Header (step screens only) -->
    <div v-if="screen === 'choose' || screen === 'payment'" class="tp-header">
      <button class="tp-back-btn" type="button" @click="goBack">
        <ArrowLeft :size="20" :stroke-width="2" />
      </button>
      <div class="tp-header-center">
        <h1 class="heading-6-1 mb-0" style="font-size:16px;color:#1C2030">Send a Tip</h1>
        <div class="tp-steps mt-1">
          <span :class="['tp-step-dot', 'tp-step-dot--active', { 'tp-step-dot--current': screen === 'choose' }]"></span>
          <span :class="['tp-step-dot', { 'tp-step-dot--active': screen === 'payment', 'tp-step-dot--current': screen === 'payment' }]"></span>
        </div>
      </div>
      <div style="width:36px"></div>
    </div>

    <!-- ─── STEP 1: Recipient + Amount ─── -->
    <div v-if="screen === 'choose'" class="tp-scroll">

      <!-- Recipient -->
      <div class="px-3 pt-4 pb-2">
        <p class="body-3 mb-2 tp-label">Recipient</p>

        <div class="tp-tab-toggle mb-3">
          <button :class="['tp-tab', { 'tp-tab--active': recipientTab === 'department' }]" type="button" @click="recipientTab = 'department'">
            <Users :size="13" :stroke-width="2" />
            Department
          </button>
          <button :class="['tp-tab', { 'tp-tab--active': recipientTab === 'staff' }]" type="button" @click="recipientTab = 'staff'">
            <User :size="13" :stroke-width="2" />
            Individual Staff
          </button>
        </div>

        <!-- Department list -->
        <div v-if="recipientTab === 'department'" class="tp-list">
          <p v-if="isLoadingDepts" class="body-3 text-center py-3 mb-0">Loading…</p>
          <p v-else-if="departments.length === 0" class="body-3 text-center py-3 mb-0">No departments found</p>
          <button
            v-for="dept in departments"
            :key="dept.id"
            :class="['tp-list-row', { 'tp-list-row--active': selectedDepartment === dept.id }]"
            type="button"
            @click="onSelectDepartment(dept.id)"
          >
            <div class="flex-grow-1">
              <p class="body-2-1 mb-0" style="color:#1C2030">{{ dept.name }}</p>
            </div>
            <span :class="['tp-radio flex-shrink-0', { 'tp-radio--active': selectedDepartment === dept.id }]">
              <Check v-if="selectedDepartment === dept.id" :size="9" :stroke-width="2.5" />
            </span>
          </button>
        </div>

        <!-- Staff list with search -->
        <div v-if="recipientTab === 'staff'">
          <div class="tp-search-wrap mb-2">
            <Search :size="14" :stroke-width="2" class="tp-search-icon" />
            <input
              v-model="staffSearch"
              class="tp-search-input body-2-2 no-focus-border"
              type="text"
              placeholder="Search by name or role…"
            />
          </div>
          <div class="tp-list">
            <p v-if="isLoadingStaff" class="body-3 text-center py-3 mb-0">Loading…</p>
            <p v-else-if="staffList.length === 0" class="body-3 text-center py-3 mb-0">No staff found</p>
            <button
              v-for="staff in staffList"
              :key="staff.id"
              :class="['tp-list-row', { 'tp-list-row--active': selectedStaff === staff.id }]"
              type="button"
              @click="onSelectStaff(staff.id)"
            >
              <div class="tp-avatar flex-shrink-0" :style="staff.image_url ? {} : { background: staff.color + '18', color: staff.color }">
                <img v-if="staff.image_url" :src="staff.image_url" style="width:100%;height:100%;object-fit:cover;border-radius:10px" />
                <span v-else>{{ staff.initials }}</span>
              </div>
              <div class="flex-grow-1">
                <p class="body-2-1 mb-0" style="color:#1C2030">{{ staff.name }}</p>
                <p class="body-3-3-1 mb-0">{{ staff.role }}</p>
              </div>
              <span :class="['tp-radio flex-shrink-0', { 'tp-radio--active': selectedStaff === staff.id }]">
                <Check v-if="selectedStaff === staff.id" :size="9" :stroke-width="2.5" />
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="tp-section-divider mx-3"></div>

      <!-- Amount -->
      <div id="tp-amount-section" class="px-3 pt-3 pb-2">
        <p class="body-3 mb-2 tp-label">
          Tip Amount
          <span class="tp-currency-badge font-semibold">IDR</span>
        </p>
        <div class="tp-presets">
          <button
            v-for="p in PRESETS"
            :key="p"
            :class="['tp-preset-chip', { 'tp-preset-chip--active': selectedPreset === p }]"
            type="button"
            @click="selectPreset(p)"
          >
            <span class="body-2-4" :style="{ color: selectedPreset === p ? 'var(--merchant-primary-color, #2B5F7A)' : '#1C2030' }">{{ formatCurrency(p) }}</span>
          </button>
          <button
            :class="['tp-preset-chip', { 'tp-preset-chip--active': selectedPreset === 'custom' }]"
            type="button"
            @click="selectPreset('custom')"
          >
            <span class="body-2-2" :style="{ color: selectedPreset === 'custom' ? 'var(--merchant-primary-color, #2B5F7A)' : '#6B7886' }">Other amount</span>
          </button>
        </div>

        <transition name="tp-slide">
          <div v-if="selectedPreset === 'custom'" class="tp-custom-input-wrap mt-3">
            <span class="body-3 tp-custom-prefix">IDR</span>
            <input
              ref="customInput"
              v-model="customAmount"
              class="tp-custom-input font-semibold no-focus-border"
              type="tel"
              placeholder="0"
              @input="onCustomInput"
            />
          </div>
        </transition>

        <p v-if="selectedPreset === 'custom' && customAmount && tipAmount < 10000" class="body-3 mt-2 mb-0" style="color:#C04A30">
          Minimum tip is IDR 10,000
        </p>
      </div>

      <div class="tp-section-divider mx-3"></div>

      <!-- Note -->
      <div class="px-3 pt-3 pb-3">
        <p class="body-3 mb-1 tp-label">
          Note
          <span class="font-regular" style="text-transform:none;letter-spacing:0;color:#aaa">(optional)</span>
        </p>
        <textarea
          v-model="note"
          class="tp-note w-100 body-2-2"
          rows="3"
          placeholder="e.g. Thank you for the warm welcome!"
        ></textarea>
      </div>

      <div class="tp-sticky-bar">
        <p v-if="inquiryError" class="body-3 text-center mb-2" style="color:#C04A30">{{ inquiryError }}</p>
        <button
          class="tp-btn-primary w-100 font-semibold d-flex align-items-center justify-content-center"
          type="button"
          :disabled="!canProceed || isLoadingInquiry"
          @click="proceedToInquiry"
        >
          <span v-if="isLoadingInquiry" class="tp-spinner mr-2"></span>
          <span>{{ isLoadingInquiry ? 'Please wait…' : 'Continue' }}</span>
        </button>
      </div>
    </div>

    <!-- ─── STEP 2: Credit Card Form ─── -->
    <div v-if="screen === 'payment' && payment_in_page" class="tp-scroll">
      <div class="px-3 pt-4">
        <XenditCardPayment :tx_payment="tx_payment" module_page="CONCIERGE_TIP" />
      </div>
    </div>

    <!-- ─── STEP 2: Payment ─── -->
    <div v-if="screen === 'payment' && !payment_in_page" class="tp-scroll">
      <div class="px-3 pt-4">

        <!-- Summary -->
        <div class="tp-summary-card mb-4">
          <div class="d-flex align-items-center" style="gap:10px">
            <Heart :size="15" :stroke-width="2" style="color:#C03060;flex-shrink:0" />
            <div class="flex-grow-1">
              <p class="body-3 mb-0 tp-label" style="margin-bottom:1px!important">Recipient</p>
              <p class="body-2-1 mb-0" style="color:#1C2030">{{ activeRecipientLabel }}</p>
            </div>
            <div class="text-right flex-shrink-0">
              <p class="body-3 mb-0 tp-label" style="margin-bottom:1px!important">Tip</p>
              <p class="body-2-4 mb-0" style="color:var(--merchant-primary-color,#2B5F7A)">{{ formatCurrency(tipAmount) }}</p>
            </div>
          </div>
          <div v-if="note" class="tp-summary-note body-3">
            "{{ note }}"
          </div>
        </div>

        <!-- Payment methods -->
        <p class="body-3 mb-2 tp-label">Payment method</p>
        <div class="tp-list mb-3">
          <p v-if="isLoadingPayments" class="body-3 text-center py-3 mb-0">Loading payment methods…</p>
          <p v-else-if="paymentMethods.length === 0" class="body-3 text-center py-3 mb-0">No payment methods available</p>
          <button
            v-for="pm in paymentMethods"
            :key="pm.id"
            :class="['tp-list-row', { 'tp-list-row--active': selectedPayment === pm.id }]"
            type="button"
            :disabled="submitting"
            @click="selectedPayment = pm.id"
          >
            <div class="tp-pay-img flex-shrink-0">
              <img :src="pm.image_url" :alt="pm.name" style="width:100%;height:100%;object-fit:contain;" />
            </div>
            <div class="flex-grow-1">
              <p class="body-2-1 mb-0" style="color:#1C2030">{{ pm.name }}</p>
              <p class="body-3-3-1 mb-0">{{ formatChannel(pm.channel) }}</p>
            </div>
            <span :class="['tp-radio flex-shrink-0', { 'tp-radio--active': selectedPayment === pm.id }]">
              <Check v-if="selectedPayment === pm.id" :size="9" :stroke-width="2.5" />
            </span>
          </button>
        </div>

        <!-- Fee breakdown -->
        <div class="tp-fee-box mb-3">
          <div class="tp-fee-row">
            <span class="body-3">Tip amount</span>
            <span class="body-3-3-2">{{ formatCurrency(tipAmount) }}</span>
          </div>
          <div v-if="surchargeAmount > 0" class="tp-fee-row">
            <span class="body-3">Surcharge</span>
            <span class="body-3-3-2">{{ formatCurrency(surchargeAmount) }}</span>
          </div>
          <div class="tp-fee-divider my-2"></div>
          <div class="tp-fee-row">
            <span class="body-2-1" style="color:#1C2030">Total</span>
            <span class="body-2-4" style="color:var(--merchant-primary-color,#2B5F7A)">{{ formatCurrency(tipTotal) }}</span>
          </div>
        </div>

      </div>

      <div class="tp-sticky-bar">
        <p v-if="submitError" class="body-3 text-center mb-2" style="color:#C04A30">{{ submitError }}</p>
        <button
          class="tp-btn-primary w-100 font-semibold d-flex align-items-center justify-content-center"
          type="button"
          :disabled="submitting || !selectedPayment"
          @click="submit"
        >
          <span v-if="submitting" class="tp-spinner mr-2"></span>
          <span>{{ submitting ? 'Processing…' : 'Pay ' + formatCurrency(tipTotal) }}</span>
        </button>
      </div>
    </div>

    <!-- ─── SUCCESS ─── -->
    <div v-if="screen === 'success'" class="tp-result-screen d-flex flex-column">
      <div class="tp-result-hero tp-result-hero--success text-center flex-shrink-0">
        <div :class="['tp-result-tick', { 'tp-result-tick--show': tickVisible }]">
          <CheckCircle :size="40" :stroke-width="1.5" />
        </div>
        <p class="body-3 mb-1" style="color:rgba(255,255,255,0.8);text-transform:uppercase;letter-spacing:0.8px">Tip sent!</p>
        <h2 class="font-bold mb-2" style="color:#fff;font-size:22px;letter-spacing:-0.2px">Thank you!</h2>
        <p class="body-3 mb-0" style="color:rgba(255,255,255,0.85);max-width:260px;margin:0 auto;line-height:1.55">
          Our team will be notified shortly. Your generosity means a lot to them.
        </p>
      </div>

      <div class="tp-result-body flex-grow-1">
        <div class="tp-receipt-card mt-4">
          <div class="text-center pb-3" style="border-bottom:1px solid #EEF1F5;margin-bottom:4px">
            <p class="body-3 mb-1 tp-label">Amount tipped</p>
            <p class="font-bold mb-0" style="font-size:26px;color:var(--merchant-primary-color,#2B5F7A);letter-spacing:-0.3px">{{ formatCurrency(savedAmount) }}</p>
          </div>
          <div v-for="row in receiptRows" :key="row.label" class="tp-receipt-row">
            <span class="body-3">{{ row.label }}</span>
            <span class="body-3-3-2 text-right" style="max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ row.value }}</span>
          </div>
        </div>
      </div>

      <div class="tp-result-footer flex-shrink-0">
        <button class="tp-btn-primary w-100 font-semibold" type="button" @click="$router.push({ name: 'ConciergeMainPage' })">
          Back to Home
        </button>
      </div>
    </div>

    <!-- ─── FAIL ─── -->
    <div v-if="screen === 'fail'" class="tp-result-screen d-flex flex-column">
      <div class="tp-result-hero tp-result-hero--fail text-center flex-shrink-0">
        <div :class="['tp-result-tick', { 'tp-result-tick--show': tickVisible }]">
          <XCircle :size="40" :stroke-width="1.5" />
        </div>
        <p class="body-3 mb-1" style="color:rgba(255,255,255,0.8);text-transform:uppercase;letter-spacing:0.8px">Payment failed</p>
        <h2 class="font-bold mb-2" style="color:#fff;font-size:22px;letter-spacing:-0.2px">Something went wrong</h2>
        <p class="body-3 mb-0" style="color:rgba(255,255,255,0.85);max-width:260px;margin:0 auto;line-height:1.55">
          We could not process your transaction. Please try again or use a different payment method.
        </p>
      </div>

      <div class="tp-result-body flex-grow-1 d-flex align-items-start justify-content-center">
        <div class="tp-fail-card w-100">
          <p class="body-3 mb-2 tp-label">Transaction details</p>
          <div class="tp-receipt-row">
            <span class="body-3">Recipient</span>
            <span class="body-3-3-2">{{ savedRecipientLabel }}</span>
          </div>
          <div class="tp-receipt-row">
            <span class="body-3">Amount</span>
            <span class="body-3-3-2">{{ formatCurrency(savedAmount) }}</span>
          </div>
          <div class="tp-receipt-row">
            <span class="body-3">Payment method</span>
            <span class="body-3-3-2">{{ savedPaymentLabel }}</span>
          </div>
        </div>
      </div>

      <div class="tp-result-footer flex-shrink-0">
        <button class="tp-btn-primary w-100 font-semibold mb-2" type="button" @click="retry">
          Try Again
        </button>
        <button class="tp-btn-ghost w-100 font-semibold" type="button" @click="$router.push({ name: 'ConciergeMainPage' })">
          Back to Home
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { ArrowLeft, Users, User, Search, Heart, Check, CheckCircle, XCircle } from 'lucide-vue'
import { get, post } from '@/services/index.js'
import { mapGetters } from 'vuex'
import { formatCurrency } from '@/utils/index.js'
import moment from 'moment'
import XenditCardPayment from '@/components/v2/XenditCardPayment'

const PRESETS = [50000, 100000, 200000]
const PAYMENT_METHOD_MODULE = 'hotel-reservation'

const AVATAR_COLORS = ['#3A8A48', '#2A8FA8', '#C04A30', '#2B6FB8', '#C03060', '#C07B20', '#6A4AC8', '#1B9E77']

function getInitials(name) {
  return name.split(' ').slice(0, 2).map(function(w) { return w[0] }).join('').toUpperCase()
}

function getAvatarColor(name) {
  var hash = 0
  for (var i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}


export default {
  name: 'ConciergeTip',

  components: { ArrowLeft, Users, User, Search, Heart, Check, CheckCircle, XCircle, XenditCardPayment },

  data() {
    return {
      PRESETS,

      screen: 'choose',

      departments: [],
      staffList: [],
      isLoadingDepts: false,
      isLoadingStaff: false,

      recipientTab: 'department',
      selectedDepartment: null,
      selectedStaff: null,
      staffSearch: '',
      note: '',

      selectedPreset: null,
      customAmount: '',

      isLoadingInquiry: false,
      inquiryError: '',
      inquiryData: null,

      paymentMethods: [],
      isLoadingPayments: false,
      selectedPayment: null,
      submitting: false,
      submitError: '',

      payment_in_page: false,
      tx_payment: {},

      savedAmount: 0,
      savedRecipientLabel: '',
      savedPaymentLabel: '',
      savedSurchargeAmount: 0,
      tickVisible: false,
    }
  },

  computed: {
    ...mapGetters(['getMerchant', 'getConciergeHomeAuth']),

    tipAmount() {
      if (!this.selectedPreset) return 0
      if (this.selectedPreset === 'custom') return parseInt(this.customAmount.replace(/\D/g, ''), 10) || 0
      return this.selectedPreset
    },
    activeSelectedPayment() {
      if (!this.selectedPayment) return null
      return this.paymentMethods.find(function(pm) { return pm.id === this.selectedPayment }.bind(this)) || null
    },
    surchargeAmount() {
      var pm = this.activeSelectedPayment
      console.log("🚀 ~ pm:", pm)
      if (!pm || !pm.surcharge_value) return 0
      if (pm.surcharge_type === 'PERCENT') {
        return Math.round(this.tipAmount * pm.surcharge_value / 100)
      }
      if (pm.surcharge_type === 'FIXED_AMOUNT') {
        return pm.surcharge_value
      }
      return 0
    },
    tipTotal() {
      return this.tipAmount + this.surchargeAmount
    },
    activeRecipient() {
      if (this.recipientTab === 'department') return this.departments.find(function(d) { return d.id === this.selectedDepartment }.bind(this)) || null
      return this.staffList.find(function(s) { return s.id === this.selectedStaff }.bind(this)) || null
    },
    activeRecipientLabel() {
      if (!this.activeRecipient) return '-'
      return this.activeRecipient.label || this.activeRecipient.name
    },
    canProceed() {
      return !!this.activeRecipient && this.tipAmount >= 10000
    },
    receiptRows() {
      var rows = [
        { label: 'Recipient',      value: this.savedRecipientLabel },
        { label: 'Payment method', value: this.savedPaymentLabel },
      ]
      if (this.savedSurchargeAmount > 0) {
        rows.push({ label: 'Surcharge', value: formatCurrency(this.savedSurchargeAmount) })
      }
      rows.push(
        { label: 'Total charged', value: formatCurrency(this.savedAmount + this.savedSurchargeAmount) },
        { label: 'Date & time',   value: moment().format('DD MMM YYYY, HH:mm') }
      )
      return rows
    },
  },

  watch: {
    recipientTab(val) {
      this.selectedDepartment = null
      this.selectedStaff = null
      this.staffSearch = ''
      if (val === 'staff' && this.staffList.length === 0) {
        this.fetchStaff('')
      }
    },
    staffSearch(val) {
      clearTimeout(this._staffSearchTimer)
      this._staffSearchTimer = setTimeout(() => {
        this.fetchStaff(val)
      }, 400)
    },
  },

  mounted() {
    var auth = this.getConciergeHomeAuth
    if (auth.reservation_status && auth.reservation_status !== 'CHECKIN') {
      this.$router.replace({ name: 'ConciergeMainPage', query: { toast: 'Available after check-in' } })
      return
    }
    this.fetchDepartments()
  },

  methods: {
    fetchDepartments() {
      this.isLoadingDepts = true
      var self = this
      get('CONCIERGE', 'department-option', { merchant_id: this.getMerchant.id, keyword: '' })
        .then(function(res) {
          if (res.data && res.data.success) {
            self.departments = res.data.data
          }
          self.isLoadingDepts = false
        })
        .catch(function() {
          self.departments = []
          self.isLoadingDepts = false
        })
    },

    fetchStaff(keyword) {
      this.isLoadingStaff = true
      var self = this
      get('CONCIERGE', 'employee-option', { merchant_id: this.getMerchant.id, keyword: keyword || '' })
        .then(function(res) {
          if (res.data && res.data.success) {
            self.staffList = res.data.data.map(function(s) {
              return {
                id: s.id,
                name: s.name,
                role: (s.position && s.position.name) ? s.position.name : '',
                initials: getInitials(s.name),
                color: getAvatarColor(s.name),
                image_url: s.image_url || '',
              }
            })
          }
          self.isLoadingStaff = false
        })
        .catch(function() {
          self.staffList = []
          self.isLoadingStaff = false
        })
    },

    formatCurrency(value) {
      return formatCurrency(value)
    },

    onSelectDepartment(id) {
      this.selectedDepartment = id
      this.scrollToAmount()
    },

    onSelectStaff(id) {
      this.selectedStaff = id
      this.scrollToAmount()
    },

    scrollToAmount() {
      this.$nextTick(() => {
        this.$smoothScroll('tp-amount-section', 'nearest')
      })
    },

    selectPreset(val) {
      this.selectedPreset = val
      if (val === 'custom') {
        this.$nextTick(() => { if (this.$refs.customInput) this.$refs.customInput.focus() })
      } else {
        this.customAmount = ''
      }
    },

    onCustomInput(e) {
      this.customAmount = e.target.value.replace(/\D/g, '')
    },

    goBack() {
      if (this.screen === 'payment') {
        this.screen = 'choose'
        this.payment_in_page = false
        this.tx_payment = {}
        this.submitError = ''
        return
      }
      this.$router.back()
    },

    proceedToInquiry() {
      if (this.isLoadingInquiry) return
      this.isLoadingInquiry = true
      this.inquiryError = ''

      var auth = this.getConciergeHomeAuth
      var params = {
        reservation_id: auth.reservation_id,
        merchant_id:    this.getMerchant.id,
        module_slug:    'tip',
        reference_type: this.recipientTab === 'department' ? 'DEPARTMENT' : 'STAFF',
        reference_id:   this.recipientTab === 'department' ? this.selectedDepartment : this.selectedStaff,
        amount:         this.tipAmount,
        notes:          this.note,
      }

      post('CONCIERGE', 'service/tx-inquiry', params, { type: 'concierge_token_direct', token: auth.token })
        .then(function(response) {
          var res = response.data
          if (res.success) {
            this.inquiryData = res.data || null
            this.fetchPaymentMethods()
            this.screen = 'payment'
          } else {
            this.inquiryError = res.message || 'Failed to proceed. Please try again.'
          }
          this.isLoadingInquiry = false
        }.bind(this))
        .catch(function() {
          this.inquiryError = 'Something went wrong. Please try again.'
          this.isLoadingInquiry = false
        }.bind(this))
    },

    fetchPaymentMethods() {
      this.isLoadingPayments = true
      this.paymentMethods = []
      this.selectedPayment = null
      var self = this
      get('CORE_HOTEL', 'merchant/' + this.getMerchant.code + '/payment-method', { module: PAYMENT_METHOD_MODULE })
        .then(function(response) {
          var res = response.data
          if (res.success) {
            self.paymentMethods = res.data
              .filter(function(pm) { return pm.pg_company === 'XENDIT' || self.$isPivotPayment(pm.pg_company) })
          }
          self.isLoadingPayments = false
        })
        .catch(function() {
          self.isLoadingPayments = false
        })
    },

    submit() {
      if (this.submitting || !this.selectedPayment) return
      this.submitting = true
      this.submitError = ''

      var auth = this.getConciergeHomeAuth
      var selected = this.paymentMethods.find(function(pm) { return pm.id === this.selectedPayment }.bind(this))
      var reservation = (this.inquiryData && this.inquiryData.ga_reservation) || {}
      var guestProfile = reservation.guest_profile || reservation.booked_by || {}

      var params = {
        merchant_id: this.getMerchant.id,
        tx_service_id: this.inquiryData.id,
        amount: this.$roundPriceByCurrency(this.tipTotal),
        customer_detail: {
          name: guestProfile.name || '',
          email: guestProfile.email || '',
          phone_number: guestProfile.phone || '',
        },
        payment_details: {
          id: selected.id,
          payment_method: selected.channel,
          bank_name: selected.pg_company,
        },
      }

      post('CONCIERGE', 'service/tx-payment', params, { type: 'concierge_token_direct', token: auth.token })
        .then(function(response) {
          var res = response.data
          if (res.success) {
            this.savedAmount = this.tipAmount
            this.savedRecipientLabel = this.activeRecipientLabel
            this.savedPaymentLabel = selected ? selected.name : ''
            this.savedSurchargeAmount = this.surchargeAmount

            if (selected.pg_company === 'XENDIT' && selected.channel === 'CREDIT_CARD') {
              var paymentData = Object.assign({}, res.payment || {})
              if (paymentData.amount && typeof paymentData.amount === 'object') {
                paymentData.amount = paymentData.amount.parsedValue || parseFloat(paymentData.amount.source) || 0
              }
              if (paymentData.duration && typeof paymentData.duration === 'object') {
                paymentData.duration = paymentData.duration.parsedValue || parseFloat(paymentData.duration.source) || 1
              }
              this.tx_payment = Object.assign({}, paymentData, { tx_service_id: this.inquiryData.id })
              this.payment_in_page = true
            } else if (this.$isPivotPayment(selected.pg_company)) {
              this.$openLink(this.$getPivotPaymentUrl(res.payment))
            } else {
              this.$router.push({
                name: 'ConciergeTipResult',
                params: { tx_service_id: this.inquiryData.id },
              })
            }
          } else {
            this.submitError = res.message || 'Payment failed. Please try again.'
          }
          this.submitting = false
        }.bind(this))
        .catch(function() {
          this.submitError = 'Something went wrong. Please try again.'
          this.submitting = false
        }.bind(this))
    },

    formatChannel(channel) {
      var map = {
        CREDIT_CARD:     'Credit Card',
        VIRTUAL_ACCOUNT: 'Virtual Account',
        EWALLET:         'E-Wallet',
        QR_CODE:         'QR Code',
        RETAIL:          'Retail Outlet',
        DIRECT_DEBIT:    'Direct Debit',
      }
      return map[channel] || channel
    },

    retry() {
      this.tickVisible = false
      this.screen = 'payment'
    },
  },
}
</script>

<style scoped>
.tp-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ── Header ── */
.tp-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid #EEF1F5;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
}

.tp-back-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #F3F4F6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1C2030;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}

.tp-header-center {
  flex: 1;
  text-align: center;
}

.tp-steps {
  display: flex;
  justify-content: center;
  gap: 5px;
}

.tp-step-dot {
  width: 6px;
  height: 6px;
  border-radius: 99px;
  background: #E0E4EC;
  transition: width 0.2s, background 0.2s;
}

.tp-step-dot--active { background: var(--merchant-primary-color, #2B5F7A); }
.tp-step-dot--current { width: 18px; }

/* ── Scrollable area ── */
.tp-scroll {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 90px;
}

/* ── Section label ── */
.tp-label {
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.tp-currency-badge {
  display: inline-block;
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  font-size: 8.5px;
  padding: 1px 6px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  vertical-align: middle;
  margin-left: 4px;
}

/* ── Divider between sections ── */
.tp-section-divider {
  height: 1px;
  background: #EEF1F5;
  margin-top: 4px;
  margin-bottom: 4px;
}

/* ── Tab toggle ── */
.tp-tab-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #F0F2F5;
  border-radius: 11px;
  padding: 3px;
  gap: 3px;
}

.tp-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-family: Nunito-SemiBold;
  font-size: 12.5px;
  color: #6B7886;
  cursor: pointer;
  transition: all 0.15s;
  -webkit-tap-highlight-color: transparent;
}

.tp-tab--active {
  background: #fff;
  color: var(--merchant-primary-color, #2B5F7A);
  box-shadow: 0 1px 4px rgba(28,32,48,0.1);
}

/* ── List rows ── */
.tp-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.tp-list-row {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1.5px solid #E4E8EF;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.14s, background 0.14s;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 2px rgba(28,32,48,0.03);
}

.tp-list-row--active {
  background: #E6EFF4;
  border-color: var(--merchant-primary-color, #2B5F7A);
}

/* ── Avatar ── */
.tp-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Nunito-Bold;
  font-size: 12px;
  letter-spacing: 0.3px;
}

/* ── Search ── */
.tp-search-wrap {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1.5px solid #E4E8EF;
  border-radius: 10px;
  padding: 0 12px;
  height: 40px;
  gap: 8px;
}

.tp-search-icon {
  color: #9EA5B4;
  flex-shrink: 0;
}

.tp-search-input {
  flex: 1;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
  font-size: 13px;
  color: #1C2030;
  min-width: 0;
}


/* ── Radio ── */
.tp-radio {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 1.5px solid #D0D5DD;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.14s;
}

.tp-radio--active {
  background: var(--merchant-primary-color, #2B5F7A);
  border-color: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
}

/* ── Presets ── */
.tp-presets {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
}

.tp-preset-chip {
  background: #fff;
  border: 1.5px solid #E4E8EF;
  border-radius: 12px;
  padding: 13px 8px;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.14s, background 0.14s;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 3px rgba(28,32,48,0.04);
}

.tp-preset-chip--active {
  background: #E6EFF4;
  border-color: var(--merchant-primary-color, #2B5F7A);
}

/* ── Custom amount input ── */
.tp-custom-input-wrap {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1.5px solid var(--merchant-primary-color, #2B5F7A);
  border-radius: 12px;
  padding: 0 16px;
  height: 52px;
}

.tp-custom-prefix {
  color: var(--merchant-primary-color, #2B5F7A);
  margin-right: 8px;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

.tp-custom-input {
  flex: 1;
  border: none;
  outline: none;
  box-shadow: none;
  font-size: 20px;
  color: #1C2030;
  background: transparent;
  min-width: 0;
}


/* ── Note ── */
.tp-note {
  background: #fff;
  border: 1.5px solid #E4E8EF;
  border-radius: 12px;
  padding: 11px 14px;
  font-size: 13px;
  color: #1C2030;
  resize: none;
  outline: none;
  line-height: 1.5;
  transition: border-color 0.15s;
}

.tp-note:focus { border-color: var(--merchant-primary-color, #2B5F7A); }

/* ── Sticky bar ── */
.tp-sticky-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #EEF1F5;
  padding: 12px 16px 20px;
  z-index: 10;
}

/* ── Buttons ── */
.tp-btn-primary {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s;
  -webkit-tap-highlight-color: transparent;
}

.tp-btn-primary:disabled { opacity: 0.4; cursor: default; }

.tp-btn-ghost {
  background: transparent;
  color: #6B7886;
  border: 1.5px solid #E4E8EF;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 13.5px;
  cursor: pointer;
  display: block;
  text-align: center;
  width: 100%;
  transition: background 0.14s;
  -webkit-tap-highlight-color: transparent;
}

/* ── Summary card ── */
.tp-summary-card {
  background: #F3F6F9;
  border-radius: 12px;
  padding: 14px;
}

.tp-summary-note {
  font-style: italic;
  color: #6B7886;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #E4E8EF;
}

/* ── Payment image ── */
.tp-pay-img {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: #F3F6F9;
  border: 1px solid #E4E8EF;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Fee box ── */
.tp-fee-box {
  background: #F3F6F9;
  border-radius: 12px;
  padding: 14px;
}

.tp-fee-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;
}

.tp-fee-divider { height: 1px; background: #E4E8EF; }

/* ── Spinner ── */
.tp-spinner {
  width: 15px;
  height: 15px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  animation: tp-spin 0.7s linear infinite;
  display: inline-block;
  flex-shrink: 0;
}

/* ── Result screens ── */
.tp-result-screen {
  position: fixed;
  inset: 0;
  z-index: 5;
  background: #FAFAFC;
  overflow: hidden;
}

.tp-result-hero {
  padding: 64px 24px 40px;
  flex-shrink: 0;
}

.tp-result-hero--success {
  background: linear-gradient(155deg, var(--merchant-primary-color, #2B5F7A) 0%, #1a3d50 100%);
}

.tp-result-hero--fail {
  background: linear-gradient(155deg, #B84030 0%, #7a1c0a 100%);
}

.tp-result-tick {
  width: 76px;
  height: 76px;
  border-radius: 999px;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin: 0 auto 14px;
  transform: scale(0.3);
  opacity: 0;
  transition: transform 0.5s cubic-bezier(0.2, 1.3, 0.4, 1), opacity 0.4s ease;
}

.tp-result-tick--show { transform: scale(1); opacity: 1; }

.tp-result-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  min-height: 0;
}

.tp-result-footer {
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid #EEF1F5;
  padding: 12px 16px 20px;
}

/* ── Receipt card ── */
.tp-receipt-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E4E8EF;
  padding: 18px;
  box-shadow: 0 2px 12px rgba(28,32,48,0.07);
}

.tp-receipt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
}

/* ── Fail card ── */
.tp-fail-card {
  background: #FFF5F3;
  border: 1px solid #FCCCC4;
  border-radius: 12px;
  padding: 14px;
  margin: 16px 0;
}

/* ── Transitions ── */
.tp-slide-enter-active, .tp-slide-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}
.tp-slide-enter, .tp-slide-leave-to { opacity: 0; max-height: 0; margin-top: 0 !important; }
.tp-slide-enter-to, .tp-slide-leave { max-height: 80px; }

@keyframes tp-spin { to { transform: rotate(360deg); } }
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/Tip.vue